import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  useTheme, 
  TextField, 
  MenuItem, 
  Card, 
  CardContent, 
  LinearProgress,
  Stack,
  Chip,
  alpha
} from '@mui/material';
import { 
  AutoAwesome, 
  ArrowForward, 
  CheckCircle, 
  Business, 
  Storefront, 
  Build, 
  Palette 
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { logFunnelEvent } from '../../firebase/analytics';

const SmartStartWebsiteHero = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Wizard State
  const [step, setStep] = useState(1); // 1: Input, 2: Generating, 3: Preview/CTA
  const [formData, setFormData] = useState({
    businessName: '',
    industry: 'consulting'
  });
  const [progress, setProgress] = useState(0);

  const industries = [
    { value: 'consulting', label: 'Consulting & Coaching', icon: <Business /> },
    { value: 'service', label: 'Home Services', icon: <Build /> },
    { value: 'wellness', label: 'Health & Wellness', icon: <AutoAwesome /> },
    { value: 'retail', label: 'Retail & E-commerce', icon: <Storefront /> },
    { value: 'creative', label: 'Creative Portfolio', icon: <Palette /> }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleStartGeneration = () => {
    if (!formData.businessName) return;
    logFunnelEvent('demo_intent', { demo: 'website', step: 1 });
    setStep(2);
    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 5;
      setProgress(currentProgress);
      if (currentProgress >= 100) {
        clearInterval(interval);
        setTimeout(() => {
          logFunnelEvent('demo_engagement', { demo: 'website' });
          setStep(3);
        }, 500);
      }
    }, 100);
  };

  const handleSaveAndSignup = () => {
    logFunnelEvent('demo_cta_click', { demo: 'website' });
    sessionStorage.setItem('websiteStartData', JSON.stringify({
      ...formData,
      timestamp: Date.now(),
      theme: 'modern'
    }));
    navigate(ROUTES.SIGNUP);
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 8, pb: 12, position: 'relative', zIndex: 1 }}>
      <Grid container spacing={{ xs: 3, md: 8 }} alignItems="center" sx={{ minHeight: '70vh' }}>
        
        {/* Left Side: Copy */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip 
              label="✨ NEW: AI Website Builder" 
              sx={{ 
                mb: 3, 
                fontWeight: 600,
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: theme.palette.secondary.light
              }} 
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '3rem', md: '4rem', lg: '4.5rem' },
                fontWeight: 800,
                lineHeight: 1,
                mb: 3,
                color: '#ffffff',
                letterSpacing: '-0.04em',
              }}
            >
              A Website That
              <br />
              <Typography
                component="span"
                variant="inherit"
                sx={{
                  color: theme.palette.secondary.main,
                  background: 'linear-gradient(to right, #fb923c, #f97316)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Actually Books.
              </Typography>
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: theme.palette.text.secondary,
                mb: 5,
                lineHeight: 1.6,
                fontWeight: 400,
                maxWidth: 540,
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}
            >
              Get a professional, SEO-optimized website that pairs perfectly with 
              your booking engine. No code, no designers, just results.
            </Typography>

            <Stack spacing={2}>
              {['Auto-generated from your business profile', 'Built-in booking widgets & forms', 'Mobile-first, lightning fast load times'].map((text) => (
                <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircle sx={{ color: theme.palette.success.main, fontSize: 20 }} />
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>{text}</Typography>
                </Box>
              ))}
            </Stack>
          </motion.div>
        </Grid>

        {/* Right Side: Smart Start Wizard */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card 
              sx={{ 
                ...theme.customEffects?.glass,
                p: { xs: 2, md: 4 },
                position: 'relative',
                overflow: 'visible',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: -1,
                  padding: '1px',
                  borderRadius: 'inherit',
                  background: 'linear-gradient(to bottom right, rgba(255,255,255,0.2), rgba(255,255,255,0))',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  pointerEvents: 'none',
                }
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                        Build Your Site
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
                        Tell us your business name to begin.
                      </Typography>
                      
                      <Stack spacing={3}>
                        <TextField
                          fullWidth
                          label="Business Name"
                          name="businessName"
                          value={formData.businessName}
                          onChange={handleInputChange}
                          placeholder="e.g. Acme Consulting"
                        />
                        
                        <TextField
                          select
                          fullWidth
                          label="Industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleInputChange}
                        >
                          {industries.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {option.icon}
                                {option.label}
                              </Box>
                            </MenuItem>
                          ))}
                        </TextField>

                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          onClick={handleStartGeneration}
                          disabled={!formData.businessName}
                          endIcon={<AutoAwesome />}
                          sx={{ 
                            py: 1.5,
                            background: theme.palette.secondary.main,
                            '&:hover': { background: theme.palette.secondary.dark }
                          }}
                        >
                          Generate Site Preview
                        </Button>
                      </Stack>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      style={{ textAlign: 'center', padding: '20px 0' }}
                    >
                      <AutoAwesome sx={{ fontSize: 60, color: theme.palette.secondary.main, mb: 3, animation: 'pulse 2s infinite' }} />
                      <Typography variant="h6" sx={{ color: 'white', mb: 3, fontWeight: 700 }}>
                        Designing Your Website...
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={progress} 
                        sx={{ 
                          height: 6, 
                          borderRadius: 3,
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '& .MuiLinearProgress-bar': {
                            background: theme.palette.secondary.main
                          }
                        }} 
                      />
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 3, display: 'block' }}>
                        Curating content • Optimizing layouts • Finalizing design
                      </Typography>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.4 }}
                    >
                      <Box sx={{ 
                        mb: 4, 
                        p: 3, 
                        borderRadius: 3, 
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <Typography variant="subtitle2" sx={{ color: theme.palette.success.light, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <CheckCircle fontSize="small" /> AI Generation Complete
                        </Typography>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, mb: 0.5 }}>
                          {formData.businessName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          Modern {formData.industry} stack deployed.
                        </Typography>
                      </Box>

                      <Typography variant="body1" sx={{ color: 'white', mb: 4, textAlign: 'center', fontWeight: 500 }}>
                        Site structure is ready! Save it to your profile.
                      </Typography>

                      <Stack spacing={2}>
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          onClick={handleSaveAndSignup}
                          endIcon={<ArrowForward />}
                          sx={{ 
                            py: 1.8, 
                            fontSize: '1.1rem',
                            background: theme.palette.success.main,
                            '&:hover': { background: theme.palette.success.dark }
                          }}
                        >
                          Save & Continue
                        </Button>
                        
                        <Button 
                          fullWidth
                          variant="text" 
                          onClick={() => setStep(1)}
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          Change Details
                        </Button>
                      </Stack>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SmartStartWebsiteHero;
